import React, { useState } from 'react';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import { useQuery, useMutation, Query } from 'react-apollo';
import { Formik, Field, FieldArray } from 'formik';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Drawer from '@material-ui/core/Drawer';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import TrashIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import Button from 'components/common/Button';
import TextField from 'components/form/Formik/FormikInput';
import Select from 'components/form/Formik/FormikReactSelect';
import Spin from 'components/common/Spin';
import useSnack from 'hooks/useSnack';
import { getFullName } from 'utils';
import {
  GET_CATEGORIES,
  ADD_CATEGORY,
  UPSERT_RATES,
  GTE_USER_RATES,
  REMOVE_RATES,
} from 'graphql/payments';

const useStyles = makeStyles(() => ({
  drawer: {
    width: 600,
    padding: 20,
  },
  close: {
    position: 'absolute',
    right: 10,
    top: 10,
  },
}));

function slugify(text) {
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/[^\w\-]+/g, '') // Remove all non-word chars
    .replace(/\-\-+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, ''); // Trim - from end of text
}

const required = (value) => (!value ? 'Required' : null);

const RATE_TYPE = [
  { label: 'Hourly', value: 'hourly' },
  { label: 'Fixed', value: 'fixed' },
];

const AccountSettings = ({ onClose, user, refetch }) => {
  const classes = useStyles();
  const snack = useSnack();
  const categoriesQueryResult = useQuery(GET_CATEGORIES);
  const [addCategory] = useMutation(ADD_CATEGORY);
  const [upsertRates] = useMutation(UPSERT_RATES);
  const [removeRates] = useMutation(REMOVE_RATES);

  const onAddCategory = (type) => async () => {
    const cat = prompt('Category name:');
    if (cat) {
      await addCategory({
        variables: {
          data: {
            type,
            label: cat,
          },
        },
      })
        .then(() => {
          categoriesQueryResult.refetch();
          snack.success('Category added');
        })
        .catch((e) => {
          snack.error(e.message);
        });
    }
  };

  const categories = categoriesQueryResult.data?.payment_categories || [];

  return (
    <Drawer open anchor="right" classes={{ paper: classes.drawer }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h5">Payment Account Details</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box marginBottom={3}>
        <Typography variant="subtitle1">{getFullName(user)}</Typography>
      </Box>
      <Query
        query={GTE_USER_RATES}
        variables={{ userId: user.id }}
        fetchPolicy={'network-only'}
      >
        {({ data, loading }) => {
          if (loading) return <Spin spinning />;

          const { rows } = data;

          const onSave = async (values, actions) => {
            const {
              client_facing_categories,
              client_facing_rate,
              non_client_facing_categories,
              non_client_facing_rate,
              completionBonus,
              miscItems,
            } = values;

            const payload = [];

            client_facing_categories.forEach((i) => {
              payload.push({
                category_id: i.value,
                rate: client_facing_rate * 100,
                rate_type: 'hourly',
                user_id: user.id,
              });
            });
            non_client_facing_categories.forEach((i) => {
              payload.push({
                category_id: i.value,
                rate: non_client_facing_rate * 100,
                rate_type: 'hourly',
                user_id: user.id,
              });
            });

            miscItems.forEach((i) => {
              payload.push({
                category_id: i.category,
                rate: i.rate * 100,
                rate_type: i.rateType,
                user_id: user.id,
              });
            });

            completionBonus.forEach((i) => {
              payload.push({
                category_id: i.category,
                rate: i.rate * 100,
                rate_type: i.rateType,
                user_id: user.id,
              });
            });

            const toRemove = [];
            rows.forEach((i) => {
              if (
                !payload.find((p) => p.category_id === i.payment_category.id)
              ) {
                toRemove.push(i.id);
              }
            });

            try {
              await upsertRates({
                variables: {
                  data: payload,
                },
              });
              if (toRemove.length) {
                await removeRates({ variables: { ids: toRemove } });
              }
              snack.success('Settings saved.');
              refetch();
              onClose();
            } catch (e) {
              snack.error(e.message);
            } finally {
              actions.setSubmitting(false);
            }
          };

          const non_client_facing_categories = rows.filter(
            (i) => i.payment_category.type === 'ncf',
          );
          const client_facing_categories = rows.filter(
            (i) => i.payment_category.type === 'cf',
          );
          const misc_categories = rows.filter(
            (i) => i.payment_category.type === 'misc',
          );
          const bonus_categories = rows.filter(
            (i) => i.payment_category.type === 'cb',
          );

          const initialValues = {
            non_client_facing_categories: non_client_facing_categories.map(
              (i) => i.payment_category,
            ),
            client_facing_categories: client_facing_categories.map(
              (i) => i.payment_category,
            ),
            non_client_facing_rate: non_client_facing_categories[0]
              ? non_client_facing_categories[0].rate / 100
              : '',
            client_facing_rate: client_facing_categories[0]
              ? client_facing_categories[0].rate / 100
              : '',
            miscItems: misc_categories.length
              ? misc_categories.map((i) => ({
                category: i.payment_category.id,
                rate: i.rate / 100,
                rateType: i.rate_type,
              }))
              : [],
            completionBonus: bonus_categories.length
              ? bonus_categories.map((i) => ({
                category: i.payment_category.id,
                rate: i.rate / 100,
                rateType: i.rate_type,
              }))
              : [],
          };

          return (
            <Box>
              <Formik initialValues={initialValues} onSubmit={onSave}>
                {({ values, handleSubmit, isSubmitting, setValues }) => {
                  return (
                    <>
                      <Box marginBottom={3}>
                        <Box
                          display="flex"
                          alignItems="center"
                          marginBottom={2}
                        >
                          <Box flex={1}>
                            <Typography variant="subtitle1">
                              Non-client facing
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center">
                            <Field
                              fullWidth
                              disableMargin
                              name="non_client_facing_rate"
                              component={TextField}
                              validate={required}
                            />
                            <span> /hour</span>
                          </Box>
                        </Box>
                        <Field
                          noMargin
                          name="non_client_facing_categories"
                          component={Select}
                          options={categories.filter((i) => i.type === 'ncf')}
                        />
                        <Button color="primary" onClick={onAddCategory('ncf')}>
                          Add Category
                        </Button>
                      </Box>
                      <Box marginBottom={3}>
                        <Box
                          display="flex"
                          alignItems="center"
                          marginBottom={2}
                        >
                          <Box flex={3}>
                            <Typography variant="subtitle1">
                              Client facing
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center">
                            <Field
                              fullWidth
                              disableMargin
                              name="client_facing_rate"
                              component={TextField}
                              validate={required}
                            />
                            <span> /hour</span>
                          </Box>
                        </Box>
                        <Field
                          noMargin
                          name="client_facing_categories"
                          component={Select}
                          options={categories.filter((i) => i.type === 'cf')}
                        />
                        <Button color="primary" onClick={onAddCategory('cf')}>
                          Add Category
                        </Button>
                      </Box>
                      <Box marginBottom={3}>
                        <Box
                          display="flex"
                          alignItems="center"
                          marginBottom={2}
                        >
                          <Typography variant="subtitle1">
                            Completion bonus{' '}
                            {!values.completionBonus.length && (
                              <Button
                                color="primary"
                                variant="outlined"
                                size="small"
                                onClick={() =>
                                  setValues({
                                    ...values,
                                    completionBonus: [{}],
                                  })
                                }
                              >
                                Add
                              </Button>
                            )}
                          </Typography>
                        </Box>
                        <FieldArray name="completionBonus">
                          {({ push, remove }) => {
                            return values.completionBonus.map((item, idx) => (
                              <Grid container key={idx} spacing={1}>
                                <Grid item md={3}>
                                  <Field
                                    transformValue
                                    isMulti={false}
                                    name={`completionBonus.${idx}.category`}
                                    component={Select}
                                    validate={required}
                                    options={categories.filter(
                                      (i) => i.type === 'cb',
                                    )}
                                  />
                                </Grid>
                                <Grid item md={3}>
                                  <Field
                                    name={`completionBonus.${idx}.rateType`}
                                    component={Select}
                                    options={RATE_TYPE}
                                    validate={required}
                                    isMulti={false}
                                    transformValue
                                  />
                                </Grid>
                                <Grid item md={3}>
                                  <Box display="flex" alignItems="center">
                                    <Field
                                      name={`completionBonus.${idx}.rate`}
                                      component={TextField}
                                      validate={required}
                                    />
                                    {item.rateType === 'hourly' && (
                                      <span>/hour</span>
                                    )}
                                  </Box>
                                </Grid>
                                <Grid item md={2}>
                                  <Box display="flex" alignItems="center">
                                    <IconButton
                                      color="secondary"
                                      onClick={() => remove(idx)}
                                    >
                                      <TrashIcon />
                                    </IconButton>
                                    {values.completionBonus.length ===
                                      idx + 1 ? (
                                      <IconButton
                                        color="primary"
                                        onClick={() => push({})}
                                      >
                                        <AddIcon />
                                      </IconButton>
                                    ) : null}
                                  </Box>
                                </Grid>
                              </Grid>
                            ));
                          }}
                        </FieldArray>
                        <Button color="primary" onClick={onAddCategory('cb')}>
                          Add Category
                        </Button>
                      </Box>
                      <Box marginBottom={3}>
                        <Box
                          display="flex"
                          alignItems="center"
                          marginBottom={2}
                        >
                          <Typography variant="subtitle1">
                            Misc{' '}
                            {!values.miscItems.length && (
                              <Button
                                color="primary"
                                variant="outlined"
                                size="small"
                                onClick={() =>
                                  setValues({ ...values, miscItems: [{}] })
                                }
                              >
                                Add
                              </Button>
                            )}
                          </Typography>
                        </Box>
                        <FieldArray name="miscItems">
                          {({ push, remove }) => {
                            return values.miscItems.map((item, idx) => (
                              <Grid container key={idx} spacing={1}>
                                <Grid item md={3}>
                                  <Field
                                    transformValue
                                    isMulti={false}
                                    name={`miscItems.${idx}.category`}
                                    component={Select}
                                    validate={required}
                                    options={categories.filter(
                                      (i) => i.type === 'misc',
                                    )}
                                  />
                                </Grid>
                                <Grid item md={3}>
                                  <Field
                                    transformValue
                                    name={`miscItems.${idx}.rateType`}
                                    component={Select}
                                    options={RATE_TYPE}
                                    validate={required}
                                    isMulti={false}
                                  />
                                </Grid>
                                <Grid item md={3}>
                                  <Box display="flex" alignItems="center">
                                    <Field
                                      name={`miscItems.${idx}.rate`}
                                      component={TextField}
                                      validate={required}
                                    />
                                    {item.rateType === 'hourly' && (
                                      <span>/hour</span>
                                    )}
                                  </Box>
                                </Grid>
                                <Grid item md={2}>
                                  <Box display="flex" alignItems="center">
                                    {idx >= 0 && (
                                      <IconButton
                                        color="secondary"
                                        onClick={() => remove(idx)}
                                      >
                                        <TrashIcon />
                                      </IconButton>
                                    )}
                                    {values.miscItems.length === idx + 1 ? (
                                      <IconButton
                                        color="primary"
                                        onClick={() => push({})}
                                      >
                                        <AddIcon />
                                      </IconButton>
                                    ) : null}
                                  </Box>
                                </Grid>
                              </Grid>
                            ));
                          }}
                        </FieldArray>

                        <Button color="primary" onClick={onAddCategory('misc')}>
                          Add Category
                        </Button>
                      </Box>
                      <Box marginTop={3}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleSubmit}
                          loading={isSubmitting}
                        >
                          Save
                        </Button>
                      </Box>
                    </>
                  );
                }}
              </Formik>
            </Box>
          );
        }}
      </Query>
    </Drawer>
  );
};

export default AccountSettings;
